import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { map, switchMap } from 'rxjs/operators';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';

import { UserType } from '@gymautoc/common/core/enums/user-type';
import { AuthService } from '@gymautoc/common/core/services/auth.service';
import { CurrentUserService } from '@gymautoc/common/core/services/current-user.service';
import { LoadingService } from '@gymautoc/common/core/services/loading.service';
import { DestroyableComponent, takeUntilDestroy } from '@gymautoc/common/core/utils/destroyable';

/** Component with information about current user. */
@Component({
    selector: 'gymautow-user-info',
    templateUrl: './user-info.component.html',
    styleUrls: ['./user-info.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatMenuTrigger,
        MatIcon,
        MatMenu,
        MatMenuItem,
        AsyncPipe,
    ],
})
@DestroyableComponent()
export class UserInfoComponent {
  private readonly userService = inject(CurrentUserService);

  private readonly authService = inject(AuthService);

  private readonly loadingService = inject(LoadingService);

  /** User. */
  public readonly user$ = this.userService.currentUser$;

  /** Change displayed name to 'Super Admin' if user is Admin. */
  public readonly displayedName$ = this.user$.pipe(
    map(user =>
      user?.type === UserType.Admin ? 'Super Admin' : `${user?.firstName} ${user?.type}`),
  );

  /** Logout user. */
  public logout(): void {
    const loaderId = this.loadingService.generateId('loading');
    this.loadingService
      .start(loaderId)
      .pipe(
        switchMap(() => this.authService.logout()),
        this.loadingService.finishInAnyCases(loaderId),
        takeUntilDestroy(this),
      )
      .subscribe();
  }
}
